import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginSchema, type LoginInput, type PublicSettings } from '@projektarbeit/shared';
import { ApiError, apiFetch } from '@/lib/api-client';
import { isValidNextPath } from '@/utils/auth-redirect';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingState } from '@/components/LoadingState';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

interface LoginResponse {
  id: number;
  email: string;
  fullName: string;
  isAdmin: boolean;
}

export function LoginPage(): JSX.Element {
  useDocumentTitle('Login · PA-Tracker');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const showIservError = params.get('error') === 'iserv';

  const settingsQuery = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => apiFetch<PublicSettings>('/public/settings'),
  });

  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  const loginMutation = useMutation({
    mutationFn: (input: LoginInput) =>
      apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      // Replace any cached unauthenticated /auth/me result before navigating.
      queryClient.invalidateQueries({ queryKey: ['auth-me'] });
      const nextParam = params.get('next');
      const target = isValidNextPath(nextParam) ? nextParam : '/dashboard';
      navigate(target, { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setServerError(err.message);
      } else {
        setServerError('Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
      }
    },
  });

  // Reset server error on input changes.
  useEffect(() => {
    const sub = form.watch(() => {
      if (serverError) setServerError(null);
    });
    return () => sub.unsubscribe();
  }, [form, serverError]);

  const showIservButton =
    settingsQuery.data?.iservSsoEnabled === true && settingsQuery.data?.iservConfigured === true;

  return (
    <main className="container max-w-md py-12">
      <h1 className="text-3xl font-semibold">Anmeldung</h1>
      {settingsQuery.data?.schoolName ? (
        <p className="mt-1 text-sm text-muted-foreground">{settingsQuery.data.schoolName}</p>
      ) : null}

      {showIservError ? (
        <div
          role="alert"
          className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          Anmeldung fehlgeschlagen. Bei Problemen den IT-Admin der Schule kontaktieren.
        </div>
      ) : null}

      <form
        className="mt-6 space-y-4"
        onSubmit={form.handleSubmit((values) => {
          setServerError(null);
          loginMutation.mutate(values);
        })}
        noValidate
      >
        <div className="space-y-1">
          <Label htmlFor="email">E-Mail</Label>
          <Input
            id="email"
            type="email"
            autoComplete="username"
            aria-invalid={Boolean(form.formState.errors.email) || undefined}
            {...form.register('email')}
          />
          {form.formState.errors.email ? (
            <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
          ) : null}
        </div>

        <div className="space-y-1">
          <Label htmlFor="password">Passwort</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            aria-invalid={Boolean(form.formState.errors.password) || undefined}
            {...form.register('password')}
          />
          {form.formState.errors.password ? (
            <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
          ) : null}
        </div>

        {serverError ? (
          <p role="alert" className="text-sm text-destructive">
            {serverError}
          </p>
        ) : null}

        <Button type="submit" className="w-full" disabled={loginMutation.isPending}>
          {loginMutation.isPending ? (
            <LoadingState variant="inline" label="Wird angemeldet …" />
          ) : (
            'Anmelden'
          )}
        </Button>
      </form>

      {showIservButton ? (
        <div className="mt-6 border-t pt-6">
          <Button asChild variant="outline" className="w-full" data-testid="iserv-login-button">
            <a href="/api/v1/auth/iserv/start">Mit IServ anmelden</a>
          </Button>
        </div>
      ) : null}
    </main>
  );
}
